interface SEOProps {
  title: string;
  description: string;
  keywords?: string[];
  canonical?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "profile";
  twitterCard?: "summary" | "summary_large_image";
  noindex?: boolean;
  children?: React.ReactNode;
}

/**
 * SEO Component for managing document head meta tags
 *
 * Uses React 19's built-in support for hoisting <title>, <meta>, and <link>
 * tags into <head> directly from component render.
 *
 * Provides comprehensive meta tag management including:
 * - Page title
 * - Meta description
 * - Keywords
 * - Canonical URL
 * - Open Graph tags (Facebook, LinkedIn)
 * - Twitter Card tags
 *
 * @example
 * <SEO
 *   title="ByteMonk - Official Technology Club of GCECT"
 *   description="Official technology club of GCECT"
 *   canonical="https://bytemonk-gcect.web.app/"
 * />
 */
export default function SEO({
  title,
  description,
  keywords = [],
  canonical,
  ogImage = "https://bytemonk-gcect.web.app/og-image.png",
  ogType = "website",
  twitterCard = "summary_large_image",
  noindex = false,
  children,
}: SEOProps) {
  const fullTitle = title.includes("ByteMonk") ? title : `${title} | ByteMonk`;

  return (
    <>
      {/* Primary Meta Tags — React 19 hoists these into <head> automatically */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />

      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Robots Meta */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {canonical && <meta property="og:url" content={canonical} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:width" content="1200" />}
      {ogImage && <meta property="og:image:height" content="630" />}
      {ogImage && (
        <meta property="og:image:alt" content={`${title} - ByteMonk`} />
      )}
      <meta property="og:site_name" content="ByteMonk" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* Additional Meta Tags */}
      <meta name="author" content="ByteMonk - GCECT Technology Club" />

      {/* Additional custom meta tags */}
      {children}
    </>
  );
}
